/**
 * Session state for the dashboard.
 *
 * The token lives in `localStorage` via the API client so a reload does not sign
 * the user out. On mount the stored token is verified against `/api/auth/me`
 * rather than trusted, so an expired token lands on the login page instead of
 * producing a dashboard whose every request fails.
 *
 * Microsoft sign-in ends with the backend redirecting here with the outcome in
 * the URL fragment (`#sso_token=…` or `#sso_error=…`). It is read once on mount
 * and removed from the address bar so the token is not left in history.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, api, getToken, setToken } from '../api/client';
import type { CurrentUser } from '../api/types';

interface AuthState {
  user: CurrentUser | null;
  /** True until the stored token has been checked. */
  loading: boolean;
  /** Error code from a failed Microsoft sign-in, for the login page. */
  ssoError: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Consume `#sso_token` / `#sso_error` left by the SSO callback redirect. */
function takeSsoResult(): string | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const ssoToken = params.get('sso_token');
  const ssoError = params.get('sso_error');
  if (!ssoToken && !ssoError) return null;

  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
  if (ssoToken) setToken(ssoToken);
  return ssoError;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      const failure = takeSsoResult();
      if (failure) setSsoError(failure);
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const current = await api.currentUser();
        if (!cancelled) setUser(current);
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) api.logout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await api.login(email, password);
    setUser(await api.currentUser());
  }, []);

  const logout = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, ssoError, login, logout }),
    [user, loading, ssoError, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Read the session. Throws when used outside `AuthProvider`. */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
